import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  BACKUP_GUARANTEES,
  BACKUP_KINDS,
  backupKindMeta,
  backupStatusMeta,
  canRestore,
  createBackup,
  deleteBackup,
  fetchBackupStats,
  formatBytes,
  listBackups,
  type CoreBackup,
} from '@/lib/coreBackup';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function BackupTab({ org }: Props) {
  const [backups, setBackups] = useState<CoreBackup[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, lastAt: null as string | null, totalBytes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [kind, setKind] = useState('full');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [b, s] = await Promise.all([listBackups(org.id), fetchBackupStats(org.id)]);
      setBackups(b);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const onBackup = async () => {
    setSaving(true);
    setError('');
    try {
      await createBackup({ organizationId: org.id, tenantId: org.tenant_id, kind, createdBy: 'dashboard', notes: `${backupKindMeta(kind).label} déclenchée manuellement.` });
      setNotice('Sauvegarde enregistrée.');
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setSaving(false); }
  };

  const onRestore = (b: CoreBackup) => {
    if (!canRestore(b)) { setError('Ce point de restauration n’est pas exploitable.'); return; }
    setNotice(`Restauration planifiée depuis ${b.restore_point} (relations préservées).`);
  };

  const remove = async (b: CoreBackup) => {
    setBusy(b.id);
    try { await deleteBackup(b.id); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Backup / Recovery</span> : le socle est compatible avec
        les sauvegardes, la restauration, la réplication et l’export. Les données critiques se restaurent
        <span className="font-semibold text-foreground-700"> sans perdre les relations</span> entre entités.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-md text-xs text-secondary-800 flex items-center justify-between"><span>{notice}</span><button onClick={() => setNotice('')} className="cursor-pointer"><i className="ri-close-line"></i></button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-archive-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Sauvegardes</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.completed}</p>
          <p className="text-xs text-foreground-500">Terminées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-hard-drive-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{formatBytes(stats.totalBytes)}</p>
          <p className="text-xs text-foreground-500">Volume</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-time-line text-lg text-accent-600"></i>
          <p className="text-sm font-bold text-foreground-900 mt-1">{stats.lastAt ? new Date(stats.lastAt).toLocaleDateString('fr-FR') : '—'}</p>
          <p className="text-xs text-foreground-500">Dernière</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-shield-check-line text-primary-600"></i>Garanties de restauration</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {BACKUP_GUARANTEES.map((g) => (
            <div key={g.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <p className="text-xs font-medium text-foreground-800 flex items-center gap-2"><i className={`${g.icon} text-primary-600`}></i>{g.label}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{g.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-archive-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Historique des sauvegardes</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : backups.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune sauvegarde.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {backups.map((b) => {
              const km = backupKindMeta(b.kind);
              const sm = backupStatusMeta(b.status);
              return (
                <div key={b.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={km.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 truncate">{b.backup_key}</p>
                    <p className="text-[11px] text-foreground-400">{km.label} · {b.tables_count || 0} tables · {formatBytes(b.size_bytes)} · {b.started_at ? new Date(b.started_at).toLocaleString('fr-FR') : ''}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                  <button onClick={() => onRestore(b)} disabled={!canRestore(b)} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Restaurer</button>
                  <button onClick={() => remove(b)} disabled={busy === b.id} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Supprimer</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-add-line text-accent-600"></i>Déclencher une sauvegarde</h4>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <select className={`${INPUT} sm:max-w-xs`} value={kind} onChange={(e) => setKind(e.target.value)}>
            {BACKUP_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label} — {k.desc}</option>)}
          </select>
          <button onClick={onBackup} disabled={saving} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2 justify-center">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>} Sauvegarder maintenant
          </button>
        </div>
      </div>
    </div>
  );
}